import { useEffect, useRef } from "react";
import * as THREE from "three";
import { buildShip, createRenderer, createWorld, projectTo } from "@/components/three/maritime";

const TAGS = [
  { id: "lidar", label: "LiDAR · EO/IR", sub: "Bow array", flip: true },
  { id: "mast", label: "Link to shore", sub: "ROC · connected" },
  { id: "crew", label: "Crew onboard", sub: "0", flip: true },
];

export const HeroScene = ({ play = true, className = "" }) => {
  const mount = useRef(null);
  const tagEls = useRef([]);
  const playRef = useRef(play);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    const el = mount.current;
    if (!el) return undefined;
    let mobile = window.matchMedia("(max-width: 767px)").matches;
    let renderer;
    try {
      renderer = createRenderer(el, mobile);
    } catch (e) {
      return undefined;
    }
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(mobile ? 50 : 32, 1, 0.5, 1400);
    const world = createWorld(scene, { segments: mobile ? 120 : 220 });
    const ship = buildShip({});
    scene.add(ship.group);
    const tagPoints = [ship.bowTop.clone().add(new THREE.Vector3(0, 0.6, 0)), ship.aftTop.clone().add(new THREE.Vector3(0, 1.2, 0)), new THREE.Vector3(2, 6.4, 0)];

    let w = 1;
    let h = 1;
    let renderStill = null;
    const resize = () => {
      w = el.clientWidth || 1;
      h = el.clientHeight || 1;
      renderer.setSize(w, h, false);
      mobile = w < 768;
      camera.aspect = w / h;
      // Keep the annotated ship in frame on narrower desktop/tablet viewports.
      const framing = Math.max(1, 1.6 / camera.aspect);
      camera.fov = mobile ? 50 : THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(32) / 2) * framing));
      // Reframe the entire scene, including its projected labels, below the nav.
      if (mobile) camera.setViewOffset(w, h, 0, h * 0.16, w, h);
      else camera.setViewOffset(w, h, -w * (w < 1280 ? 0.12 : 0.2), h * 0.07, w, h);
      camera.updateProjectionMatrix();
      renderStill?.();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMove = (e) => {
      pointer.tx = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.ty = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    let visible = true;
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    io.observe(el);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const clock = new THREE.Clock();
    const tmp = new THREE.Vector3();
    let start = null;
    let raf = 0;

    const frame = () => {
      const t = reduce ? 0 : clock.getElapsedTime();
      if (playRef.current && start === null) start = t;
      const k = start === null ? 0 : Math.min((t - start) / 4.2, 1);
      const e = reduce ? 1 : 1 - Math.pow(1 - k, 3);
      world.update(t);
      world.uniforms.uIntro.value = e;
      ship.update(t);
      ship.setSensorLevel(e, t);
      pointer.x += (pointer.tx - pointer.x) * 0.035;
      pointer.y += (pointer.ty - pointer.y) * 0.035;
      const scrollK = Math.min(window.scrollY / Math.max(window.innerHeight, 1), 1.3);
      // Keep the gentle camera drift inside the annotated composition.
      const ang = 2.42 - Math.sin(t * 0.035) * 0.08 + pointer.x * 0.09;
      const radius = (mobile ? 84 : 80) + (1 - e) * 60;
      camera.position.set(Math.cos(ang) * radius, 17 + (1 - e) * 26 + scrollK * 30 - pointer.y * 2.4, Math.sin(ang) * radius);
      camera.lookAt(2, 1.5 - scrollK * 8, 0);
      renderer.render(scene, camera);
      tagPoints.forEach((p, i) => {
        const node = tagEls.current[i];
        if (!node) return;
        const s = projectTo(p, ship.group, camera, w, h, tmp);
        node.style.transform = `translate3d(${s.x}px, ${s.y}px, 0)`;
        node.style.opacity = String(Math.max(0, (e - 0.55) / 0.45) * (s.behind ? 0 : 1));
      });
    };
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (visible) frame();
    };
    if (reduce) {
      renderStill = frame;
      frame();
    } else tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      world.dispose();
      ship.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return (
    <div className={`overflow-hidden ${className}`} aria-hidden="true" data-testid="hero-3d-scene">
      <div ref={mount} className="absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 hidden md:block">
        {TAGS.map((tag, i) => (
          <div key={tag.id} ref={(n) => { tagEls.current[i] = n; }} className="absolute left-0 top-0 opacity-0 will-change-transform" data-testid={`hero-tag-${tag.id}`}>
            <div className={`-translate-y-full pb-2 ${tag.flip ? "-translate-x-full" : ""}`}>
              <div className={`flex items-end gap-2 ${tag.flip ? "flex-row-reverse" : ""}`}>
                <span className="mb-[-5px] h-10 w-px bg-gradient-to-t from-signal to-transparent" />
                <div className="rounded-md border border-white/10 bg-ink/60 px-2.5 py-1.5 backdrop-blur-md">
                  <p className="mono text-[11px] uppercase tracking-[0.14em] text-fg">{tag.label}</p>
                  <p className="mono text-[11px] uppercase tracking-[0.14em] text-signal">{tag.sub}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
